/**
 * SH Updated: Add script to handle variant options
 * Made to Order Collection ID = 483790749925
 * In Stock Collection ID = 483790717157
 *
 * When a user clicks a product from either "made to order" or "in stock" collection, the url will have a "collection_id" parameter
 * 
 * If the "collection_id" parameter is present, then the script will check the "variant_options" data-variant-options on the page
 * 
 * if the "collection_id" = 483790717157, then the script will check the "available" field of the variant_options
 * if the "quantity" is 0, then the script will hide the unavailable options in the variant-selects
 * if the "quantity" is greater than 0, then the script will keep the variant in the variant_options and show the available options in the variant-selects
 *
 * if the "collection_id" = 483790749925, then the script will check the "available" field of the variant_options
 * if the "quantity" is 0, then the script will hide the variant options in the variant_options and show the available options in the variant-selects
 * if the "quantity" is greater than 0, then the script will show the unavailable options in the variant-selects
 * 
 * This script dynamically handles products with single or multiple options.
 */

// Collection IDs
const IN_STOCK_COLLECTION_ID = '483790717157';
const MADE_TO_ORDER_COLLECTION_ID = '483790749925';

// Guard flags to prevent infinite loops
let isValidating = false;
let isCascading = false;
let isHandlingDOMReplacement = false;
let isImageClickNavigation = false;
let targetVariantIdOverride = null;
let clickedImageMediaId = null; // Store the media ID of the clicked image

/**
 * Helper function to restore the clicked image as active in the media gallery
 */
function restoreClickedImageAsActive() {
  if (!clickedImageMediaId) {
    console.log('[SH Image Nav] No clicked image ID to restore');
    return;
  }

  const mediaGallery = document.querySelector('media-gallery');
  if (!mediaGallery) {
    console.error('[SH Image Nav] Media gallery not found');
    return;
  }

  console.log(`[SH Image Nav] Restoring clicked image as active: ${clickedImageMediaId}`);

  // Method 1: Try setActiveMedia if available
  if (mediaGallery.setActiveMedia) {
    console.log('[SH Image Nav] Using setActiveMedia method');
    mediaGallery.setActiveMedia(clickedImageMediaId, true);
  }

  // Method 2: Also manually set the active class and scroll
  const targetMediaItem = mediaGallery.querySelector(`li[data-media-id="${clickedImageMediaId}"]`);
  if (targetMediaItem) {
    console.log('[SH Image Nav] Found target media item, manually activating...');

    // Remove active class from all items
    mediaGallery.querySelectorAll('li[data-media-id]').forEach(item => {
      item.classList.remove('is-active');
    });

    // Add active class to target
    targetMediaItem.classList.add('is-active');

    // Scroll into view
    targetMediaItem.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'start' });

    // Also update thumbnail if exists
    const thumbnails = mediaGallery.querySelector('[id^="GalleryThumbnails"]');
    if (thumbnails) {
      thumbnails.querySelectorAll('button').forEach(btn => btn.classList.remove('is-active'));
      const targetThumb = thumbnails.querySelector(`button[data-target="${clickedImageMediaId}"]`);
      if (targetThumb) {
        targetThumb.classList.add('is-active');
      }
    }

    console.log('[SH Image Nav] Manual activation complete');
  } else {
    console.error(`[SH Image Nav] Could not find media item with ID: ${clickedImageMediaId}`);
  }
}

/**
 * Intercept product-info's methods to control behavior during image click navigation
 */
function setupUpdateURLInterceptor() {
  const productInfo = document.querySelector('product-info');
  if (!productInfo) {
    console.warn('[SH Image Nav] product-info element not found, retrying in 500ms...');
    setTimeout(setupUpdateURLInterceptor, 500);
    return;
  }

  if (productInfo._shOriginalUpdateURL) {
    console.log('[SH Image Nav] interceptors already installed');
    return; // Already intercepted
  }

  // Store original methods
  productInfo._shOriginalUpdateURL = productInfo.updateURL.bind(productInfo);
  productInfo._shOriginalUpdateOptionValues = productInfo.updateOptionValues.bind(productInfo);

  // Override updateURL to use our target variant ID
  productInfo.updateURL = function(url, variantId) {
    if (isImageClickNavigation && targetVariantIdOverride) {
      console.log(`[SH Image Nav] Intercepting updateURL: using ${targetVariantIdOverride} instead of ${variantId}`);
      productInfo._shOriginalUpdateURL(url, targetVariantIdOverride);
    } else {
      productInfo._shOriginalUpdateURL(url, variantId);
    }
  };

  // Override updateOptionValues to skip DOM replacement during image click navigation
  productInfo.updateOptionValues = function(html) {
    if (isImageClickNavigation) {
      console.log('[SH Image Nav] Skipping updateOptionValues - preventing DOM replacement of dropdowns');
      return; // Don't replace the dropdowns we just set
    }
    productInfo._shOriginalUpdateOptionValues(html);
  };

  // Override updateMedia to restore clicked image after Shopify's update
  if (productInfo.updateMedia) {
    productInfo._shOriginalUpdateMedia = productInfo.updateMedia.bind(productInfo);
    productInfo.updateMedia = function(html, variantFeaturedMediaId) {
      if (isImageClickNavigation && clickedImageMediaId) {
        console.log('[SH Image Nav] Letting updateMedia run, then restoring clicked image...');
        // Let Shopify update the media first
        productInfo._shOriginalUpdateMedia(html, variantFeaturedMediaId);

        // Then restore the clicked image as active
        setTimeout(() => {
          restoreClickedImageAsActive();
        }, 100);
        return;
      }
      productInfo._shOriginalUpdateMedia(html, variantFeaturedMediaId);
    };
  }

  console.log('[SH Image Nav] interceptors installed successfully');
}

/**
 * SH MEDIA GALLERY FILTERING
 *
 * Filters product media gallery based on collection-specific variant availability.
 * Hides images for variants that don't match "In Stock" or "Made to Order" criteria.
 *
 * Dependencies: media-gallery.js, product-info.js, data-variant-options JSON
 */

/**
 * Normalize image URLs for comparison (removes protocol and query params)
 * Example: "//domain.com/image.jpg?v=123&width=1000" → "domain.com/image.jpg"
 */
function normalizeImageUrl(url) {
  if (!url) return '';
  return url.replace(/^https?:/, '').replace(/^\/\//, '').split('?')[0];
}

/**
 * Build Set of image URLs from filtered variants for O(1) lookup
 * @param {Array} filteredVariants - Variants that passed collection filtering
 * @returns {Set<string>} Normalized image URLs
 */
function buildValidImageUrlsSet(filteredVariants) {
  const validUrls = new Set();
  filteredVariants.forEach(variant => {
    if (variant.variant_image) {
      const normalizedUrl = normalizeImageUrl(variant.variant_image);
      validUrls.add(normalizedUrl);
      console.log(`[SH Media Filter] Valid variant: "${variant.variant_name}" → Image: ${normalizedUrl}`);
    }
  });
  console.log(`[SH Media Filter] Total valid image URLs: ${validUrls.size}`, Array.from(validUrls));
  return validUrls;
}

/**
 * Filter media gallery to show only images for valid variants
 * - Hides media items that don't match validImageUrls
 * - Syncs thumbnails
 * - Sets first valid image as active using MediaGallery API
 *
 * @param {Set<string>} validImageUrls - Normalized URLs from buildValidImageUrlsSet()
 */
function filterMediaGallery(validImageUrls) {
  if (!validImageUrls || validImageUrls.size === 0) {
    console.log('[SH Media Filter] No valid image URLs provided, skipping filtering');
    return;
  }

  const mediaGallery = document.querySelector('media-gallery');
  if (!mediaGallery) {
    console.warn('[SH Media Filter] Media gallery element not found');
    return;
  }

  // Get viewer and thumbnails (pattern from media-gallery.js)
  const viewer = mediaGallery.querySelector('[id^="GalleryViewer"]');
  const thumbnails = mediaGallery.querySelector('[id^="GalleryThumbnails"]');
  if (!viewer) {
    console.warn('[SH Media Filter] Gallery viewer not found');
    return;
  }

  const mediaItems = viewer.querySelectorAll('li[data-media-id]');
  console.log(`[SH Media Filter] Processing ${mediaItems.length} media items in gallery`);

  let firstValidMediaId = null;
  let visibleCount = 0;
  let hiddenCount = 0;

  mediaItems.forEach((mediaItem, index) => {
    // Extract and normalize image URL
    const img = mediaItem.querySelector('img');
    const imgSrc = img?.currentSrc || img?.src;
    const normalizedSrc = normalizeImageUrl(imgSrc);

    const isValid = validImageUrls.has(normalizedSrc);

    console.log(`[SH Media Filter] Media ${index + 1}: ${isValid ? '✓ SHOW' : '✗ HIDE'} - ${normalizedSrc}`);

    // Show/hide media item
    if (isValid) {
      mediaItem.style.display = '';
      mediaItem.classList.remove('sh-filtered-out');
      if (!firstValidMediaId) firstValidMediaId = mediaItem.dataset.mediaId;
      visibleCount++;
    } else {
      mediaItem.style.display = 'none';
      mediaItem.classList.add('sh-filtered-out');
      hiddenCount++;
    }

    // Sync corresponding thumbnail
    if (thumbnails) {
      const thumbnail = thumbnails.querySelector(`li[data-target="${mediaItem.dataset.mediaId}"]`);
      if (thumbnail) {
        thumbnail.style.display = isValid ? '' : 'none';
        console.log(`[SH Media Filter] Thumbnail ${index + 1}: ${isValid ? 'SHOW' : 'HIDE'}`);
      }
    }
  });

  console.log(`[SH Media Filter] Gallery filtering complete: ${visibleCount} visible, ${hiddenCount} hidden`);

  // Set first valid media as active (uses MediaGallery API from media-gallery.js)
  if (firstValidMediaId && mediaGallery.setActiveMedia) {
    console.log(`[SH Media Filter] Setting active media: ${firstValidMediaId}`);
    mediaGallery.setActiveMedia(firstValidMediaId, true);
  } else if (!firstValidMediaId) {
    console.warn('[SH Media Filter] No valid media found to set as active');
  }
}

/**
 * IMAGE CLICK HANDLER
 * 
 * Handles image clicks when zoom is disabled ("none").
 * Updates variant selection to match the clicked image.
 */

/**
 * Show a toast notification to the user
 * @param {string} message - The message to display
 * @param {string} type - 'info', 'warning', or 'error'
 * @param {number} duration - How long to show the toast (ms)
 */
function showToast(message, type = 'info', duration = 3000) {
  // Remove any existing toast
  const existingToast = document.getElementById('sh-image-nav-toast');
  if (existingToast) {
    existingToast.remove();
  }

  // Create toast element
  const toast = document.createElement('div');
  toast.id = 'sh-image-nav-toast';
  toast.innerHTML = `
    <span class="sh-toast-icon">${type === 'info' ? 'ℹ️' : type === 'warning' ? '⚠️' : '❌'}</span>
    <span class="sh-toast-message">${message}</span>
  `;

  // Apply styles
  toast.style.cssText = `
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: ${type === 'info' ? '#333' : type === 'warning' ? '#856404' : '#721c24'};
    color: ${type === 'info' ? '#fff' : type === 'warning' ? '#fff' : '#fff'};
    padding: 12px 24px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    z-index: 10000;
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    opacity: 0;
    transition: opacity 0.3s ease;
  `;

  document.body.appendChild(toast);

  // Fade in
  requestAnimationFrame(() => {
    toast.style.opacity = '1';
  });

  // Fade out and remove after duration
  setTimeout(() => {
    toast.style.opacity = '0';
    setTimeout(() => {
      toast.remove();
    }, 300);
  }, duration);
}

// Helper: Clean image URL for fuzzy matching (remove size suffixes like _500x, _1000x)
function cleanImageUrlForMatching(url) {
  if (!url) return '';
  // Remove protocol, query params, and size suffixes like _100x, _1024x1024, etc.
  return url.replace(/^https?:/, '').replace(/^\/\//, '').split('?')[0].replace(/_[0-9]+x[0-9]*(\.[a-zA-Z]+)$/, '$1').replace(/_[0-9]+x(\.[a-zA-Z]+)$/, '$1');
}

// Helper: Extract just the base filename (without path and UUID suffixes) for looser matching
function extractBaseFilename(url) {
  if (!url) return '';
  // Get just the filename from the path
  const cleanUrl = cleanImageUrlForMatching(url);
  const parts = cleanUrl.split('/');
  const filename = parts[parts.length - 1];

  // Remove common UUID/hash suffixes like _b246dfa3-51f3-41cf-b612-cd0879ff9e05
  // Pattern: underscore followed by hex characters (8-4-4-4-12 UUID pattern or similar)
  const baseFilename = filename.replace(/_[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi, '');

  return baseFilename;
}

function setupImageClickNavigation() {
  const productInfo = document.querySelector('product-info');
  if (!productInfo) return;

  // Check if image zoom is disabled via data attribute
  const zoomType = productInfo.getAttribute('data-zoom-type');
  if (zoomType !== 'none') {
    console.log('[SH Image Nav] Image navigation disabled: Zoom is enabled (' + zoomType + ')');
    return;
  }

  console.log('[SH Image Nav] Initializing image-to-variant navigation (Zoom: none)');

  // Use event delegation on the document to handle dynamically loaded content
  document.addEventListener('click', function(event) {
    // Check if clicked element is an image inside the main gallery viewer
    const clickedImage = event.target.closest('[id^="GalleryViewer"] .product__media-item img');
    
    if (!clickedImage) return;

    const clickTime = new Date().toISOString().split('T')[1]; // Just the time portion
    console.log('[SH Image Nav] ================== IMAGE CLICK START ==================');
    console.log(`[SH Image Nav] Time: ${clickTime}`);
    console.log('[SH Image Nav] Image clicked:', clickedImage.src);

    // Check if guard flag is already active (from previous click)
    if (isImageClickNavigation) {
      console.warn('[SH Image Nav] ⚠️ Guard flag already ACTIVE - previous click still processing. Ignoring this click.');
      console.warn('[SH Image Nav] ⚠️ Try waiting a moment before clicking again.');
      return;
    }

    // Capture the media ID of the clicked image for later restoration
    const clickedMediaItem = clickedImage.closest('li[data-media-id]');
    if (clickedMediaItem) {
      clickedImageMediaId = clickedMediaItem.dataset.mediaId;
      console.log('[SH Image Nav] Captured clicked media ID:', clickedImageMediaId);
    } else {
      clickedImageMediaId = null;
      console.warn('[SH Image Nav] Could not find media ID for clicked image');
    }

    // Get variant data
    const variantDataScript = document.querySelector('[data-variant-options]');
    if (!variantDataScript) return;

    let variantData;
    try {
      variantData = JSON.parse(variantDataScript.textContent);
    } catch (e) {
      console.error('[SH Image Nav] Failed to parse variant data', e);
      return;
    }

    // Get collection ID for filtering context
    const urlParams = new URLSearchParams(window.location.search);
    const collectionId = urlParams.get('collection_id');

    // 1. Identify Matching Variants
    const clickedUrlClean = cleanImageUrlForMatching(clickedImage.currentSrc || clickedImage.src);
    const clickedBaseFilename = extractBaseFilename(clickedImage.currentSrc || clickedImage.src);
    console.log('[SH Image Nav] Cleaned Image URL:', clickedUrlClean);
    console.log('[SH Image Nav] Base filename:', clickedBaseFilename);

    // Debug: Log all variants and their images
    console.log('[SH Image Nav] All variants in data:');
    variantData.variant_options.forEach((v, i) => {
      const cleanUrl = cleanImageUrlForMatching(v.variant_image);
      const baseFilename = extractBaseFilename(v.variant_image);
      const exactMatch = cleanUrl === clickedUrlClean ? '✓ EXACT' : '';
      const fuzzyMatch = (!exactMatch && baseFilename === clickedBaseFilename) ? '~ FUZZY' : '';
      console.log(`  ${i}: "${v.variant_name}" (qty: ${v.quantity}) → ${cleanUrl} ${exactMatch}${fuzzyMatch}`);
    });

    // Filter variants that use this image AND match current collection filters
    const matchingVariants = variantData.variant_options.filter(variant => {
      if (!variant.variant_image) return false;

      // Match image - try exact match first, then fuzzy match on base filename
      const variantImageClean = cleanImageUrlForMatching(variant.variant_image);
      const variantBaseFilename = extractBaseFilename(variant.variant_image);

      const isExactMatch = variantImageClean === clickedUrlClean;
      const isFuzzyMatch = variantBaseFilename === clickedBaseFilename;

      if (!isExactMatch && !isFuzzyMatch) return false;

      // Respect collection filters if active
      if (collectionId === IN_STOCK_COLLECTION_ID) {
        return parseInt(variant.quantity) > 0;
      } else if (collectionId === MADE_TO_ORDER_COLLECTION_ID) {
        return parseInt(variant.quantity) <= 0;
      }
      
      return true;
    });

    console.log(`[SH Image Nav] Found ${matchingVariants.length} matching variants for this image`);

    if (matchingVariants.length === 0) {
      console.warn('[SH Image Nav] No valid variants found for this image in current context');
      showToast('This image is not linked to a specific variant', 'info', 2500);
      return;
    }

    // 2. Select Best Match (Option B logic)
    // Try to keep currently selected options where possible
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) {
      console.error('[SH Image Nav] ❌ variant-selects element NOT FOUND on page!');
      return;
    }
    
    const selectsFound = variantSelects.querySelectorAll('select');
    if (selectsFound.length === 0) {
      console.error('[SH Image Nav] ❌ No select elements found inside variant-selects!');
      return;
    }
    console.log(`[SH Image Nav] Found ${selectsFound.length} select dropdown(s)`);
    
    const currentSelections = Array.from(selectsFound).map(s => s.value);
    
    let bestMatch = null;
    let maxScore = -1;

    matchingVariants.forEach(variant => {
      const options = variant.variant_name.split(' / ').map(s => s.trim());
      let score = 0;
      
      // Calculate score based on matching current selections
      options.forEach((opt, idx) => {
        if (opt === currentSelections[idx]) score++;
      });

      if (score > maxScore) {
        maxScore = score;
        bestMatch = variant;
      }
    });

    if (!bestMatch) bestMatch = matchingVariants[0]; // Fallback

    console.log('[SH Image Nav] Selected Variant:', bestMatch.variant_name, bestMatch.variant_id);

    // 3. Update UI and URL
    // Set guard flag BEFORE making changes to prevent other handlers from interfering
    isImageClickNavigation = true;
    targetVariantIdOverride = bestMatch.variant_id;
    console.log('[SH Image Nav] Guard flag SET, target variant override:', targetVariantIdOverride);

    const selectElements = variantSelects.querySelectorAll('select');
    const targetOptions = bestMatch.variant_name.split(' / ').map(s => s.trim());
    
    // Log current state of all dropdowns BEFORE changes
    console.log('[SH Image Nav] Current dropdown values BEFORE change:');
    selectElements.forEach((select, idx) => {
      console.log(`  Dropdown ${idx} (${select.name || 'unnamed'}): "${select.value}"`);
    });
    console.log('[SH Image Nav] Target values:', targetOptions);
    
    let lastChangedSelect = null;
    let changesAttempted = 0;
    let changesSucceeded = 0;

    // Update all select elements to target values
    selectElements.forEach((select, idx) => {
      const targetValue = targetOptions[idx];
      if (!targetValue) {
        console.log(`[SH Image Nav] Dropdown ${idx}: No target value for this position, skipping`);
        return;
      }
      
      if (select.value === targetValue) {
        console.log(`[SH Image Nav] Dropdown ${idx}: Already set to "${targetValue}", no change needed`);
        return;
      }
      
      changesAttempted++;
      const oldValue = select.value;
      
      // Check if target option exists in dropdown
      const targetOption = Array.from(select.options).find(opt => opt.value === targetValue);
      if (!targetOption) {
        console.error(`[SH Image Nav] ❌ Dropdown ${idx}: Target value "${targetValue}" NOT FOUND in options!`);
        console.log(`[SH Image Nav] Available options:`, Array.from(select.options).map(o => o.value));
        return;
      }

      // Check if target option is disabled or marked unavailable
      const isDisabled = targetOption.disabled;
      const isHidden = targetOption.style.display === 'none';
      const isUnavailable = targetOption.textContent.includes('Unavailable');

      if (isDisabled || isHidden || isUnavailable) {
        console.warn(`[SH Image Nav] ⚠️ Dropdown ${idx}: Target option "${targetValue}" has issues:`);
        console.warn(`  - disabled: ${isDisabled}, hidden: ${isHidden}, unavailable text: ${isUnavailable}`);
        console.warn(`[SH Image Nav] ⚠️ This variant may not be purchasable!`);
      }
      
      // Attempt to set the value
      select.value = targetValue;
      
      // Verify the change took effect
      if (select.value === targetValue) {
        console.log(`[SH Image Nav] ✓ Dropdown ${idx}: Changed from "${oldValue}" to "${targetValue}"`);
        lastChangedSelect = select;
        changesSucceeded++;
      } else {
        console.error(`[SH Image Nav] ❌ Dropdown ${idx}: Failed to change! Still "${select.value}" (tried "${targetValue}")`);
      }
    });

    console.log(`[SH Image Nav] Changes: ${changesSucceeded}/${changesAttempted} succeeded`);
    
    // Log state AFTER changes
    console.log('[SH Image Nav] Current dropdown values AFTER change:');
    selectElements.forEach((select, idx) => {
      console.log(`  Dropdown ${idx}: "${select.value}"`);
    });

    if (lastChangedSelect || changesAttempted === 0) {
      // We have the correct variant ID - update URL directly to avoid race condition
      const targetVariantId = bestMatch.variant_id;
      const currentUrl = new URL(window.location.href);
      const currentVariantId = currentUrl.searchParams.get('variant');
      
      console.log(`[SH Image Nav] Target variant ID: ${targetVariantId}`);
      console.log(`[SH Image Nav] Current URL variant ID: ${currentVariantId}`);
      
      if (currentVariantId !== String(targetVariantId)) {
        // Update URL with correct variant ID BEFORE dispatching change event
        currentUrl.searchParams.set('variant', targetVariantId);
        const newUrl = currentUrl.toString();
        console.log('[SH Image Nav] Updating URL to:', newUrl);
        window.history.replaceState({}, '', newUrl);
      } else {
        console.log('[SH Image Nav] URL already has correct variant ID');
      }
      
      if (lastChangedSelect) {
        console.log('[SH Image Nav] Dispatching change event to trigger product-info refresh...');
        // Dispatch change event to trigger variant-selects and product-info update
        // Use a small delay to ensure URL is updated first
        setTimeout(() => {
          console.log('[SH Image Nav] Change event dispatched NOW');
          lastChangedSelect.dispatchEvent(new Event('change', { bubbles: true }));

          // Reset guard flag after variant-selects and product-info.js have had time to process
          // This needs to be longer than the fetch + DOM replacement cycle (~500-1000ms)
          setTimeout(() => {
            // Always ensure the clicked image is restored as active
            // (in case updateMedia wasn't called by Shopify due to cascading availability issues)
            restoreClickedImageAsActive();

            isImageClickNavigation = false;
            targetVariantIdOverride = null;
            clickedImageMediaId = null;
            console.log('[SH Image Nav] Guard flag CLEARED');
            console.log('[SH Image Nav] Final URL:', window.location.href);
            console.log('[SH Image Nav] ================== IMAGE CLICK END ==================');
          }, 1500);
        }, 10);
      } else {
        // No dropdown changes needed, but URL might need update
        console.log('[SH Image Nav] ℹ️ Dropdown values already correct');
        // Still trigger a refresh if URL was updated
        if (currentVariantId !== String(targetVariantId)) {
          console.log('[SH Image Nav] Triggering refresh for URL update...');
          selectElements[0].dispatchEvent(new Event('change', { bubbles: true }));
        }
        setTimeout(() => {
          // Always ensure the clicked image is restored as active
          restoreClickedImageAsActive();

          isImageClickNavigation = false;
          targetVariantIdOverride = null;
          clickedImageMediaId = null;
          console.log('[SH Image Nav] Guard flag CLEARED');
          console.log('[SH Image Nav] Final URL:', window.location.href);
          console.log('[SH Image Nav] ================== IMAGE CLICK END ==================');
        }, 1500);
      }
    } else {
      // changesAttempted > 0 but changesSucceeded === 0 (all changes failed)
      console.error('[SH Image Nav] ❌ Changes attempted but none succeeded!');
      // Still try to restore the clicked image
      restoreClickedImageAsActive();

      isImageClickNavigation = false;
      targetVariantIdOverride = null;
      clickedImageMediaId = null;
      console.log('[SH Image Nav] Guard flag CLEARED (error state)');
      console.log('[SH Image Nav] ================== IMAGE CLICK END ==================');
    }
  });
}


// Function to apply variant filtering
function applyVariantFiltering() {
  // Get collection ID from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const collectionId = urlParams.get('collection_id');

  console.log('[SH Variant Filter] ========================================');
  console.log('[SH Variant Filter] Starting variant filtering on page load');
  console.log('[SH Variant Filter] ========================================');

  // Only proceed if collection_id parameter is present
  if (!collectionId || (collectionId !== IN_STOCK_COLLECTION_ID && collectionId !== MADE_TO_ORDER_COLLECTION_ID)) {
    console.log('[SH Variant Filter] No valid collection_id found, skipping filtering');
    return;
  }
  
  // Get variant options data
  const variantDataScript = document.querySelector('[data-variant-options]');
  if (!variantDataScript) {
    console.warn('SH Variant Options: No variant data found');
    return;
  }
  
  let variantData;
  try {
    variantData = JSON.parse(variantDataScript.textContent);
  } catch (error) {
    console.error('SH Variant Options: Error parsing variant data', error);
    return;
  }
  
  // Get variant selects element
  const variantSelects = document.querySelector('variant-selects');
  if (!variantSelects) {
    console.warn('SH Variant Options: No variant-selects element found');
    return;
  }
  
  // Function to determine if a variant should be included based on quantity
  function shouldIncludeVariant(collectionId, quantity) {
    const hasStock = parseInt(quantity) > 0;
    
    if (collectionId === IN_STOCK_COLLECTION_ID) {
      // In Stock collection: include only variants with quantity > 0
      return hasStock;
    } else if (collectionId === MADE_TO_ORDER_COLLECTION_ID) {
      // Made to Order collection: include only variants with quantity = 0
      return !hasStock;
    }
    return true; // Default: include all variants
  }
  
  // Filter variants based on collection criteria
  const filteredVariants = variantData.variant_options.filter(variant => {
    return shouldIncludeVariant(collectionId, variant.quantity);
  });

  console.log(`[SH Variant Filter] Collection ID: ${collectionId}`);
  console.log(`[SH Variant Filter] Total variants: ${variantData.variant_options.length}, Filtered variants: ${filteredVariants.length}`);
  filteredVariants.forEach(variant => {
    console.log(`[SH Variant Filter] ✓ "${variant.variant_name}" (quantity: ${variant.quantity})`);
  });

  if (filteredVariants.length === 0) {
    console.warn('SH Variant Options: No variants match the filter criteria');
    return;
  }
  
  // Build a map of valid option values per option position
  // This works for products with any number of options
  const validOptionValuesByPosition = {};
  
  filteredVariants.forEach(variant => {
    if (variant.options && Array.isArray(variant.options)) {
      // Parse the variant name to extract individual option values
      // Variant name format: "Value1 / Value2 / Value3"
      const variantNameParts = variant.variant_name.split(' / ').map(part => part.trim());

      variant.options.forEach((option, optionIndex) => {
        if (!validOptionValuesByPosition[optionIndex]) {
          validOptionValuesByPosition[optionIndex] = {
            optionName: option.option_name,
            values: new Set()
          };
        }

        // Add only the option value at this specific position
        if (variantNameParts[optionIndex]) {
          validOptionValuesByPosition[optionIndex].values.add(variantNameParts[optionIndex]);
        }
      });
    }
  });
  
  // Get all select elements (one for each option)
  const selectElements = variantSelects.querySelectorAll('select');

  console.log(`[SH Variant Filter] Processing ${selectElements.length} option dropdowns`);

  selectElements.forEach((selectElement, selectIndex) => {
    const validValues = validOptionValuesByPosition[selectIndex];

    if (!validValues) {
      console.warn(`SH Variant Options: No valid values found for select at index ${selectIndex}`);
      return;
    }

    console.log(`[SH Variant Filter] Dropdown "${validValues.optionName}": ${validValues.values.size} valid values`, Array.from(validValues.values));

    // Process each option in this select
    const optionElements = selectElement.querySelectorAll('option');
    let visibleCount = 0;
    
    optionElements.forEach(optionElement => {
      const optionValue = optionElement.value;

      // Skip empty values (placeholder options)
      if (!optionValue) {
        return;
      }

      // Check if this option value is in the valid set (case-sensitive exact match)
      let isValid = false;
      for (const validValue of validValues.values) {
        if (validValue === optionValue) {
          // Additional checks: ensure option is available and doesn't have unavailable text
          const isAvailable = optionElement.getAttribute('data-sh-option-value-available') !== 'false';
          const hasUnavailableText = optionElement.textContent.includes('- Unavailable');

          if (isAvailable && !hasUnavailableText) {
            isValid = true;
          }
          break;
        }
      }

      if (isValid) {
        optionElement.style.display = 'block';
        optionElement.removeAttribute('disabled');
        visibleCount++;
      } else {
        optionElement.style.display = 'none';
        optionElement.setAttribute('disabled', 'disabled');
      }
    });

    console.log(`[SH Variant Filter] Dropdown "${validValues.optionName}": ${visibleCount} options visible`);

    // Handle the case where the currently selected option is invalid
    const selectedOption = selectElement.querySelector('option:checked');
    const isSelectedInvalid = selectedOption && (
      selectedOption.style.display === 'none' ||
      selectedOption.getAttribute('data-sh-option-value-available') === 'false' ||
      selectedOption.textContent.includes('- Unavailable')
    );

    if (isSelectedInvalid) {
      // Find the first truly available option (not just visible)
      let firstAvailableOption = null;
      for (const option of selectElement.options) {
        if (option.value &&
            option.style.display !== 'none' &&
            !option.disabled &&
            option.getAttribute('data-sh-option-value-available') !== 'false' &&
            !option.textContent.includes('- Unavailable')) {
          firstAvailableOption = option;
          break;
        }
      }

      if (firstAvailableOption) {
        firstAvailableOption.selected = true;
        // Trigger change event to update the product
        selectElement.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });

  // Apply cascading filtering based on current selections
  applyCascadingFiltering(collectionId, variantData, filteredVariants);

  // Validate and auto-correct selected variant if needed
  validateAndCorrectSelectedVariant(collectionId, variantData, filteredVariants);

  // Filter media gallery based on collection criteria
  const validImageUrls = buildValidImageUrlsSet(filteredVariants);
  filterMediaGallery(validImageUrls);

  console.log('[SH Variant Filter] ========================================');
  console.log('[SH Variant Filter] Filtering complete on page load');
  console.log('[SH Variant Filter] ========================================');
}

// Function to apply cascading filtering based on selected options
function applyCascadingFiltering(collectionId, variantData, filteredVariants) {
  // Check guard flags to prevent infinite loops
  if (isCascading || isValidating) {
    return;
  }

  // Get variant selects element
  const variantSelects = document.querySelector('variant-selects');
  if (!variantSelects) {
    return;
  }

  // Get all select elements
  const selectElements = variantSelects.querySelectorAll('select');
  if (selectElements.length === 0) {
    return;
  }

  // Get current selections from all selects
  const currentSelections = {};
  selectElements.forEach((selectElement, index) => {
    const value = selectElement.value;
    if (value && value.trim() !== '') {
      currentSelections[index] = value.trim();
    }
  });

  // If no selections, skip cascading (show all valid options)
  if (Object.keys(currentSelections).length === 0) {
    return;
  }

  // Set guard flag
  isCascading = true;

  // For each select that has a selection, filter the OTHER selects
  Object.keys(currentSelections).forEach(selectedIndex => {
    const selectedIndexNum = parseInt(selectedIndex);
    const selectedValue = currentSelections[selectedIndex];

    // Find variants that match this selection AND are in filteredVariants
    const matchingVariants = filteredVariants.filter(variant => {
      const variantParts = variant.variant_name.split(' / ').map(part => part.trim());
      // Case-sensitive exact match
      return variantParts[selectedIndexNum] &&
             variantParts[selectedIndexNum] === selectedValue;
    });

    if (matchingVariants.length === 0) {
      return;
    }

    // Extract valid values for OTHER select positions
    const validValuesByPosition = {};
    matchingVariants.forEach(variant => {
      const variantParts = variant.variant_name.split(' / ').map(part => part.trim());
      variantParts.forEach((part, index) => {
        if (index !== selectedIndexNum) { // Skip the selected position
          if (!validValuesByPosition[index]) {
            validValuesByPosition[index] = new Set();
          }
          validValuesByPosition[index].add(part);
        }
      });
    });

    // Apply filtering to OTHER selects
    selectElements.forEach((selectElement, selectIndex) => {
      // Skip the select that triggered the cascading
      if (selectIndex === selectedIndexNum) {
        return;
      }

      const validValues = validValuesByPosition[selectIndex];
      if (!validValues) {
        return;
      }

      // Process each option in this select
      const optionElements = selectElement.querySelectorAll('option');
      let visibleCount = 0;

      optionElements.forEach(optionElement => {
        const optionValue = optionElement.value;

        // Skip empty values (placeholder options)
        if (!optionValue) {
          return;
        }

        // Check if this option value is in the valid set (case-sensitive exact match)
        let isValid = false;
        for (const validValue of validValues) {
          if (validValue === optionValue) {
            // Additional checks: ensure option is available and doesn't have unavailable text
            const isAvailable = optionElement.getAttribute('data-sh-option-value-available') !== 'false';
            const hasUnavailableText = optionElement.textContent.includes('- Unavailable');

            if (isAvailable && !hasUnavailableText) {
              isValid = true;
            }
            break;
          }
        }

        if (isValid) {
          optionElement.style.display = 'block';
          optionElement.removeAttribute('disabled');
          visibleCount++;
        } else {
          optionElement.style.display = 'none';
          optionElement.setAttribute('disabled', 'disabled');
        }
      });

      // Handle the case where the currently selected option is invalid
      const selectedOption = selectElement.querySelector('option:checked');
      const isSelectedInvalid = selectedOption && (
        selectedOption.style.display === 'none' ||
        selectedOption.getAttribute('data-sh-option-value-available') === 'false' ||
        selectedOption.textContent.includes('- Unavailable')
      );

      if (isSelectedInvalid) {
        // Find the first truly available option (not just visible)
        let firstAvailableOption = null;
        for (const option of selectElement.options) {
          if (option.value &&
              option.style.display !== 'none' &&
              !option.disabled &&
              option.getAttribute('data-sh-option-value-available') !== 'false' &&
              !option.textContent.includes('- Unavailable')) {
            firstAvailableOption = option;
            break;
          }
        }

        if (firstAvailableOption) {
          firstAvailableOption.selected = true;
        } else {
          console.warn(`SH Variant Options: No available option found to auto-select for select ${selectIndex}`);
        }
      }
    });
  });

  // Reset guard flag
  isCascading = false;
}

// Function to validate the selected variant and auto-correct if invalid
function validateAndCorrectSelectedVariant(collectionId, variantData, filteredVariants) {
  // Check guard flag to prevent infinite loops
  if (isValidating) {
    return;
  }

  // Get the currently selected variant data from the page
  const selectedVariantScript = document.querySelector('[data-selected-variant]');
  if (!selectedVariantScript) {
    return;
  }
  
  let selectedVariant;
  try {
    selectedVariant = JSON.parse(selectedVariantScript.textContent);
  } catch (error) {
    console.error('SH Variant Options: Error parsing selected variant', error);
    return;
  }
  
  if (!selectedVariant || !selectedVariant.id) {
    return;
  }
  
  // Find the selected variant in our variant data
  const selectedVariantData = variantData.variant_options.find(v => 
    v.variant_id == selectedVariant.id
  );
  
  if (!selectedVariantData) {
    console.warn('SH Variant Options: Selected variant not found in variant data');
    return;
  }
  
  // Check if this variant is valid for the current collection
  const hasStock = parseInt(selectedVariantData.quantity) > 0;
  let isValidForCollection = false;
  
  if (collectionId === IN_STOCK_COLLECTION_ID) {
    isValidForCollection = hasStock;
  } else if (collectionId === MADE_TO_ORDER_COLLECTION_ID) {
    isValidForCollection = !hasStock;
  }
  
  if (isValidForCollection) {
    return;
  }

  // Set guard flag before making changes
  isValidating = true;

  // Selected variant is INVALID - we need to auto-select a valid alternative
  console.warn('SH Variant Options: Selected variant is INVALID for this collection, finding alternative...');
  
  if (filteredVariants.length === 0) {
    console.error('SH Variant Options: No valid variants available to switch to');
    return;
  }
  
  // Get current selections
  const variantSelects = document.querySelector('variant-selects');
  const selectElements = variantSelects.querySelectorAll('select');
  const currentSelections = Array.from(selectElements).map(select => select.value);
  
  // Try to find a variant that matches as many current selections as possible
  let bestMatch = null;
  let bestMatchScore = -1;
  
  filteredVariants.forEach(variant => {
    const variantParts = variant.variant_name.split(' / ').map(p => p.trim());
    let matchScore = 0;
    
    // Count how many options match current selections
    currentSelections.forEach((selection, index) => {
      if (variantParts[index] === selection) {
        matchScore++;
      }
    });
    
    if (matchScore > bestMatchScore) {
      bestMatchScore = matchScore;
      bestMatch = variant;
    }
  });
  
  if (!bestMatch) {
    // If no match found, just use the first valid variant
    bestMatch = filteredVariants[0];
  }
  
  // Parse the best match variant to get the option values to select
  const targetSelections = bestMatch.variant_name.split(' / ').map(p => p.trim());
  
  // Update the select elements to match the valid variant
  let optionChanged = false;
  selectElements.forEach((selectElement, index) => {
    const targetValue = targetSelections[index];
    if (selectElement.value !== targetValue) {
      // Find the option with this value
      const targetOption = Array.from(selectElement.options).find(opt => opt.value === targetValue);
      if (targetOption && !targetOption.disabled) {
        selectElement.value = targetValue;
        optionChanged = true;
      }
    }
  });
  
  // If we changed any options, trigger a change event to update the product
  if (optionChanged) {
    selectElements[0].dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Reset guard flag after a delay to allow the change event to complete
  setTimeout(() => {
    isValidating = false;
  }, 1000);
}

// Run on initial page load
document.addEventListener('DOMContentLoaded', function() {
  applyVariantFiltering();
  setupImageClickNavigation();
  setupUpdateURLInterceptor();
  
  // Multiple approaches to detect variant changes
  
  // Approach 1: Listen for the variantChange event from product-info.js
  // This fires AFTER product-info.js replaces the DOM with fresh HTML
  if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function() {
      // Skip if image click navigation is in progress - let the image click handler control everything
      if (isImageClickNavigation) {
        console.log('[SH Variant Filter] Skipping variantChange handler - image click navigation in progress');
        return;
      }

      // Set guard flag to prevent cascading/validation from triggering during DOM replacement handling
      isHandlingDOMReplacement = true;

      // Wait for HTMLUpdateUtility.viewTransition to complete (~150ms)
      setTimeout(() => {

        // Re-apply only the initial filtering (not cascading/validation to avoid loops)
        const urlParams = new URLSearchParams(window.location.search);
        const collectionId = urlParams.get('collection_id');

        if (!collectionId || (collectionId !== IN_STOCK_COLLECTION_ID && collectionId !== MADE_TO_ORDER_COLLECTION_ID)) {
          isHandlingDOMReplacement = false;
          return;
        }

        const variantDataScript = document.querySelector('[data-variant-options]');
        if (!variantDataScript) {
          isHandlingDOMReplacement = false;
          return;
        }

        let variantData;
        try {
          variantData = JSON.parse(variantDataScript.textContent);
        } catch (error) {
          isHandlingDOMReplacement = false;
          return;
        }

        const variantSelects = document.querySelector('variant-selects');
        if (!variantSelects) {
          isHandlingDOMReplacement = false;
          return;
        }

        // Filter variants
        const filteredVariants = variantData.variant_options.filter(variant => {
          const hasStock = parseInt(variant.quantity) > 0;
          if (collectionId === IN_STOCK_COLLECTION_ID) {
            return hasStock;
          } else if (collectionId === MADE_TO_ORDER_COLLECTION_ID) {
            return !hasStock;
          }
          return true;
        });

        if (filteredVariants.length === 0) {
          isHandlingDOMReplacement = false;
          return;
        }

        // Build valid options map
        const validOptionValuesByPosition = {};
        filteredVariants.forEach(variant => {
          if (variant.options && Array.isArray(variant.options)) {
            const variantNameParts = variant.variant_name.split(' / ').map(part => part.trim());
            variant.options.forEach((option, optionIndex) => {
              if (!validOptionValuesByPosition[optionIndex]) {
                validOptionValuesByPosition[optionIndex] = {
                  optionName: option.option_name,
                  values: new Set()
                };
              }
              if (variantNameParts[optionIndex]) {
                validOptionValuesByPosition[optionIndex].values.add(variantNameParts[optionIndex]);
              }
            });
          }
        });

        // Apply filtering to ALL selects in the fresh DOM
        const selectElements = variantSelects.querySelectorAll('select');
        selectElements.forEach((selectElement, selectIndex) => {
          const validValues = validOptionValuesByPosition[selectIndex];
          if (!validValues) return;

          const optionElements = selectElement.querySelectorAll('option');
          optionElements.forEach(optionElement => {
            const optionValue = optionElement.value;
            if (!optionValue) return;

            let isValid = false;
            for (const validValue of validValues.values) {
              if (validValue.toLowerCase() === optionValue.toLowerCase()) {
                const isAvailable = optionElement.getAttribute('data-sh-option-value-available') !== 'false';
                const hasUnavailableText = optionElement.textContent.includes('- Unavailable');
                if (isAvailable && !hasUnavailableText) {
                  isValid = true;
                }
                break;
              }
            }

            if (isValid) {
              optionElement.style.display = 'block';
              optionElement.removeAttribute('disabled');
            } else {
              optionElement.style.display = 'none';
              optionElement.setAttribute('disabled', 'disabled');
            }
          });
        });

        // Now apply cascading filtering based on current selections in the fresh DOM
        applyCascadingFiltering(collectionId, variantData, filteredVariants);

        // Re-filter gallery after DOM update from product-info.js
        const validImageUrls = buildValidImageUrlsSet(filteredVariants);
        filterMediaGallery(validImageUrls);

        // Reset guard flag
        isHandlingDOMReplacement = false;
      }, 150); // Wait for DOM replacement to complete
    });
  }
  
  // Approach 2: Listen directly to select element changes
  setTimeout(() => {
    const selectElements = document.querySelectorAll('variant-selects select');
    selectElements.forEach(selectElement => {
      selectElement.addEventListener('change', function() {
        // Skip if already cascading, validating, handling DOM replacement, or image click navigation to prevent loops
        if (isCascading || isValidating || isHandlingDOMReplacement || isImageClickNavigation) {
          console.log('[SH Variant Filter] Skipping change handler - guard flag active');
          return;
        }

        setTimeout(() => {
          applyVariantFiltering();
        }, 100); // Reduced delay for faster cascading response
      });
    });
  }, 1000);
  
  // Approach 3: MutationObserver fallback
  const observer = new MutationObserver(function(mutations) {
    // Skip if we're already handling DOM replacement or image click navigation
    if (isHandlingDOMReplacement || isImageClickNavigation) {
      console.log('[SH Variant Filter] Skipping MutationObserver - guard flag active');
      return;
    }

    mutations.forEach(function(mutation) {
      // Check if variant-selects content was updated
      if (mutation.target.closest && mutation.target.closest('variant-selects')) {
        setTimeout(() => {
          applyVariantFiltering();
        }, 200);
      }
    });
  });
  
  // Start observing variant-selects for changes
  const variantSelects = document.querySelector('variant-selects');
  if (variantSelects) {
    observer.observe(variantSelects, {
      childList: true,
      subtree: true,
      attributes: false
    });
  }
});
